import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export const WALLET_FUND_IDS = {
  topupCard: "topup-card",
  topupAmount: "topup-amount-input",
  topupSubmit: "topup-submit-button",
  interacCard: "interac-deposit-card",
  interacAmount: "interac-amount-input",
  interacSubmit: "interac-submit-button",
  interacReference: "interac-reference-code",
  interacDepositsList: "interac-deposits-list",
};

export default function WalletFundingPanel({ onFunded }) {
  const [topup, setTopup] = useState("10.00");
  const [topupBusy, setTopupBusy] = useState(false);
  const [interac, setInterac] = useState("10.00");
  const [interacNote, setInteracNote] = useState("");
  const [interacBusy, setInteracBusy] = useState(false);
  const [lastRef, setLastRef] = useState(null);
  const [deposits, setDeposits] = useState([]);

  const loadDeposits = async () => {
    try {
      const { data } = await api.get("/wallet/interac/deposits");
      setDeposits(data.deposits || []);
    } catch (err) {
      // Non-fatal — the panel still renders without history.
      console.warn("interac history failed", err);
    }
  };

  useEffect(() => {
    loadDeposits();
  }, []);

  const submitTopup = async (e) => {
    e.preventDefault();
    const cents = Math.round(parseFloat(topup || "0") * 100);
    if (cents < 100) return toast.error("Minimum top-up is 1.00 CAD");
    setTopupBusy(true);
    try {
      const { data } = await api.post("/wallet/topup", {
        amount_cents: cents,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Top-up failed");
      setTopupBusy(false);
    }
  };

  const submitInterac = async (e) => {
    e.preventDefault();
    const cents = Math.round(parseFloat(interac || "0") * 100);
    if (cents < 100) return toast.error("Minimum Interac deposit is 1.00 CAD");
    setInteracBusy(true);
    try {
      const { data } = await api.post("/wallet/interac/deposit", {
        amount_cents: cents,
        note: interacNote.trim() || null,
      });
      setLastRef(data);
      setInteracNote("");
      toast.success(`Reference code: ${data.reference}`);
      await loadDeposits();
      onFunded?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Interac request failed");
    } finally {
      setInteracBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      <form
        data-testid={WALLET_FUND_IDS.topupCard}
        onSubmit={submitTopup}
        className="telesis-panel rounded p-6 space-y-3"
      >
        <p className="telesis-eyebrow">Fund wallet · Stripe (card)</p>
        <p className="text-xs text-slate-400 leading-relaxed">
          Add CAD to your Telesis wallet instantly. Funds appear in your balance after Stripe confirms payment and can be used to finance posts or cashed out later.
        </p>
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Label className="text-xs uppercase tracking-wider text-slate-400">Amount (CAD)</Label>
            <Input
              data-testid={WALLET_FUND_IDS.topupAmount}
              type="number"
              min="1"
              step="0.5"
              value={topup}
              onChange={(e) => setTopup(e.target.value)}
              className="mt-1 bg-[#12141c] border-[#222634] text-slate-100 font-mono"
            />
          </div>
          <Button
            data-testid={WALLET_FUND_IDS.topupSubmit}
            type="submit"
            disabled={topupBusy}
            className="rounded-full bg-yellow-400 text-black hover:bg-yellow-300 font-semibold"
          >
            {topupBusy ? "Opening…" : "Add funds"}
          </Button>
        </div>
      </form>

      <form
        data-testid={WALLET_FUND_IDS.interacCard}
        onSubmit={submitInterac}
        className="telesis-panel rounded p-6 space-y-3"
      >
        <p className="telesis-eyebrow">Fund wallet · Interac e-Transfer</p>
        <p className="text-xs text-slate-400 leading-relaxed">
          Register a deposit intent and we&apos;ll give you a reference code to include in your e-Transfer memo. Funds are credited once staff reconciles the transfer.
        </p>
        <div className="grid md:grid-cols-2 gap-2">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-400">Amount (CAD)</Label>
            <Input
              data-testid={WALLET_FUND_IDS.interacAmount}
              type="number"
              min="1"
              step="0.5"
              value={interac}
              onChange={(e) => setInterac(e.target.value)}
              className="mt-1 bg-[#12141c] border-[#222634] text-slate-100 font-mono"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-400">Note (optional)</Label>
            <Textarea
              value={interacNote}
              onChange={(e) => setInteracNote(e.target.value)}
              rows={1}
              className="mt-1 bg-[#12141c] border-[#222634] text-slate-100 resize-none"
            />
          </div>
        </div>
        <Button
          data-testid={WALLET_FUND_IDS.interacSubmit}
          type="submit"
          disabled={interacBusy}
          className="rounded-full bg-yellow-400 text-black hover:bg-yellow-300 font-semibold w-full"
        >
          {interacBusy ? "Registering…" : "Get Interac reference"}
        </Button>
        {lastRef && (
          <div className="mt-3 p-3 rounded bg-emerald-500/5 border border-emerald-500/30 text-xs text-emerald-200 space-y-1">
            <p>Send ${(lastRef.amount_cents / 100).toFixed(2)} CAD by Interac e-Transfer to:</p>
            <p className="font-mono text-sm text-emerald-100">{lastRef.payee_email}</p>
            <p>Memo must include:</p>
            <p data-testid={WALLET_FUND_IDS.interacReference} className="font-mono text-sm text-yellow-300">
              {lastRef.reference}
            </p>
          </div>
        )}
        {deposits.length > 0 && (
          <ul data-testid={WALLET_FUND_IDS.interacDepositsList} className="mt-3 divide-y divide-white/5 text-xs">
            {deposits.slice(0, 5).map((d) => (
              <li key={d.deposit_id} className="py-2 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-slate-200 font-mono truncate">{d.reference} · {d.status}</p>
                  <p className="text-[10px] font-mono text-slate-500 uppercase tracking-widest">
                    {new Date(d.created_at).toLocaleDateString()}
                  </p>
                </div>
                <span className="font-mono text-yellow-300">${(d.amount_cents / 100).toFixed(2)}</span>
              </li>
            ))}
          </ul>
        )}
      </form>
    </div>
  );
}
